import { createRoot } from "react-dom/client";
import { createElement } from "react";
import { FeedbackWidget } from "./FeedbackWidget";
import {
  WIDGET_VERSION,
  WIDGET_COMMIT_SHORT,
  WIDGET_VERSION_LABEL,
} from "./version";
import type { FeedbackPosition, FeedbackUser } from "./types";

declare global {
  interface Window {
    feedbackHubUser?: FeedbackUser;
  }
}

// Surface build metadata for host-page debugging. The IIFE globalName is
// `FeedbackHub` (set in tsup.config.ts), so `window.FeedbackHub.version`
// works from a console regardless of bundler.
(() => {
  if (typeof window === "undefined") return;
  type FH = { version?: string; commit?: string; label?: string };
  const w = window as unknown as { FeedbackHub?: FH };
  w.FeedbackHub = {
    ...(w.FeedbackHub ?? {}),
    version: WIDGET_VERSION,
    commit: WIDGET_COMMIT_SHORT,
    label: WIDGET_VERSION_LABEL,
  };
})();

(function mount() {
  if (typeof document === "undefined") return;
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>("script[data-key]");
  if (!script) {
    console.warn("[feedback-hub] could not locate <script data-key=…> tag");
    return;
  }

  const apiKey = script.getAttribute("data-key");
  if (!apiKey) {
    console.warn("[feedback-hub] missing data-key attribute");
    return;
  }

  const endpoint =
    script.getAttribute("data-endpoint") ?? new URL(script.src).origin;
  const positionAttr = script.getAttribute("data-position");
  const position: FeedbackPosition =
    positionAttr === "bottom-left" ? "bottom-left" : "bottom-right";
  const accent = script.getAttribute("data-accent") ?? undefined;
  const label = script.getAttribute("data-label") ?? undefined;

  const parseNum = (attr: string): number | undefined => {
    const v = script.getAttribute(attr);
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const offset = {
    bottom: parseNum("data-bottom"),
    side: parseNum("data-side"),
    bottomMobile: parseNum("data-bottom-mobile"),
  };
  const hasOffset =
    offset.bottom !== undefined ||
    offset.side !== undefined ||
    offset.bottomMobile !== undefined;

  const container = document.createElement("div");
  container.setAttribute("data-feedback-hub-root", "");
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(
    createElement(FeedbackWidget, {
      apiKey,
      endpoint,
      position,
      theme: accent ? { accent } : undefined,
      label,
      user: window.feedbackHubUser,
      offset: hasOffset ? offset : undefined,
    }),
  );
})();
